import type { Permission } from '@care-platform/api-client';
import { EmptyState, LoadingState } from '@care-platform/ui';
import type { ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { useCopy } from '../copy';
import { AppLayout } from '../layout/app-layout';
import { SessionProvider, useSession } from '../session';
import { RouteLink } from './context';
import type { RouteRegistry } from './registry';

/** Shows the screen only when the user's permissions include what it needs. The API enforces it too. */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission | undefined;
  children: ReactNode;
}) {
  const copy = useCopy();
  const session = useSession();
  if (permission && !session.can(permission)) {
    return (
      <EmptyState title={copy.shell.forbiddenTitle} description={copy.shell.forbiddenBody} className="m-4" />
    );
  }
  return <>{children}</>;
}

function NotFound({ homeRouteId }: { homeRouteId: string }) {
  const copy = useCopy();
  return (
    <div className="m-4">
      <EmptyState title={copy.shell.notFoundTitle} description={copy.shell.notFoundBody} className="px-0" />
      <RouteLink to={homeRouteId} className="text-sm text-brand-blue hover:underline">
        {copy.shell.goHome}
      </RouteLink>
    </div>
  );
}

function RouteLoading() {
  const copy = useCopy();
  return <LoadingState label={copy.common.loading} className="m-4" />;
}

/**
 * The route tree for a shell: the session and layout around every module screen, each screen lazy-loaded at the
 * path the shell mounted it, the home route redirecting to `homeRouteId`, and a not-found screen.
 */
export function buildShellRoutes(registry: RouteRegistry, homeRouteId: string): RouteObject[] {
  const home = registry.href(homeRouteId);
  if (!home) throw new Error(`The home route "${homeRouteId}" is not mounted in this shell.`);

  const screens: RouteObject[] = registry.routes.map(({ route, mount, pattern }) => ({
    id: route.id,
    path: pattern,
    lazy: async () => {
      const { default: Screen } = await route.load();
      const permission = route.permission ?? mount.module.permissions[0];
      return {
        element: (
          <RequirePermission permission={permission}>
            <Screen />
          </RequirePermission>
        ),
      };
    },
  }));

  return [
    {
      element: (
        <SessionProvider>
          <AppLayout />
        </SessionProvider>
      ),
      hydrateFallbackElement: <RouteLoading />,
      children: [
        { index: true, element: <Navigate to={home} replace /> },
        ...screens,
        { path: '*', element: <NotFound homeRouteId={homeRouteId} /> },
      ],
    },
  ];
}
